import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ResetTemplate,
  ConfirmationTemplate,
} from '../../modules/users/components';
import { PasswordResetRequestDTO } from '../../modules/users/infra/types/RequestPasswordReset.type';
import { resetPassword } from '../../modules/users/redux/operators';
import verifyResetToken from '../../modules/users/redux/operators/verifyResetToken';
import { LoadingModal } from '../../shared/components/loader';
import { useAppDispatch, useAppSelector } from '../../shared/infra/redux';
import { BasicLayout } from '../../shared/layout';

type ResetPasswordsProps = {
  token?: string;
};

function PasswordResetPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const isValidToken = useAppSelector(
    (state) => state.public.resetTokenIsValid,
  );

  const [isSuccessful, setIsSuccessful] = useState<boolean | undefined>(
    undefined,
  );

  const params = useParams<ResetPasswordsProps>();
  const { token } = params;

  const handleOnVerifyToken = useCallback(
    async (token?: string) => {
      await dispatch(verifyResetToken({ token }));
    },
    [dispatch],
  );

  const handleOnSubmit = useCallback(
    async (
      request: PasswordResetRequestDTO,
      cb: (error: string | null, success: boolean) => void,
    ) => {
      const resultAction = await dispatch(resetPassword(request));
      if (resetPassword.rejected.match(resultAction)) {
        cb(
          resultAction.payload
            ? resultAction.payload.message
            : 'Error trying to reset your new password',
          false,
        );
      }

      if (resetPassword.fulfilled.match(resultAction)) {
        setIsSuccessful(true);
        cb(null, true);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    void handleOnVerifyToken(token);
  }, [handleOnVerifyToken, token]);

  return (
    <BasicLayout>
      <div className='flex items-center justify-center flex-1 w-full px-8 mt-28 lg:w-1/3 lg:px-0 lg:mt-0 mx-auto'>
        {isValidToken === undefined && <LoadingModal isLoading={true} />}

        {isValidToken === false && (
          <ConfirmationTemplate
            isError={true}
            title='Invalid Reset Token'
            subtitle='This token is either invalid or has expired'
          />
        )}

        {isValidToken === true && isSuccessful === true && (
          <ConfirmationTemplate
            title='Your Password Has Been Reset'
            subtitle='You can now login to your account with your new password'
          />
        )}

        {isValidToken === true && isSuccessful === false && (
          <ConfirmationTemplate
            isError={true}
            title='Error Reseting Your Password'
            subtitle='There was an error trying to update your password'
          />
        )}

        {isValidToken === true && isSuccessful === undefined && (
          <ResetTemplate onSubmit={handleOnSubmit} token={token ? token : ''} />
        )}
      </div>
    </BasicLayout>
  );
}

export default PasswordResetPage;
